// types.ts — tipos y constantes compartidas del chat de generación de Novah.
// Qué hace: define los tipos del flujo (steps, mensajes, estilos) y las constantes
//   de UI (opciones de formato/idioma, mapeos de formato por red, helper suggestFormat).
// Qué exporta: tipos (ChatMsg, Step, ImageStyle, BakedStyleOption, ContactDraft, UserPlan)
//   + constantes (CONTACT_STYLE_ID, PLATFORMS, FORMAT_OPTIONS, LANGUAGE_OPTIONS) + suggestFormat.
// Quién lo usa: useGenerateChat, NovahGenerateChat y los widgets de widgets/.

import { SocialNetwork, PreviewFormat } from '@agency-smm/shared';
import { PLATFORM_LIST } from '../../utils/networkIcons';

// Estilo built-in que renderiza datos de contacto en el poster. Si está entre
// los elegidos y faltan datos, Novah los pide antes de generar.
export const CONTACT_STYLE_ID = 'bold-service-ad';

export interface BakedStyleOption {
  id: string;
  label: string;
  preview: string;
  custom: boolean;
  // El estilo reserva una zona verde para meter una foto real → habilita el
  // toggle "Auto-fill the green zone" en la generación.
  isGreenbox?: boolean;
  // Rubro recomendado (texto libre que carga el admin en el editor de estilos).
  // null / ausente = sin tag.
  industry?: string | null;
}

// Datos de contacto que Novah puede pedir/guardar para los AI-styled posters.
export interface ContactDraft {
  address: string;
  phoneNumber: string;
  businessHours: string;
  whatsapp: string;
  email: string;
}

export interface ChatMsg {
  id: number;
  from: 'novah' | 'user';
  text: string;
  widget?: string;
}

export type Step =
  | 'platforms' | 'count' | 'format' | 'prompt' | 'language' | 'style'
  | 'photos' | 'ai-model' | 'baked-style' | 'ai-transfer' | 'contact' | 'confirm' | 'generating' | 'done';

export const PLATFORMS = PLATFORM_LIST;

export type ImageStyle = 'stock' | 'pastel' | 'asset' | 'ai' | 'mix' | 'styled' | 'transfer';

export type UserPlan = 'free' | 'pro' | 'business';

// Formato del post (aspect ratio). El tipo de contenido (education / promotion / etc)
// ya no se pregunta acá — se toma del content_mix configurado en el brand profile.
export const FORMAT_OPTIONS: { id: PreviewFormat; label: string; icon: string; hint: string }[] = [
  { id: 'square', label: '1:1', icon: '⬛', hint: 'Instagram/Facebook Feed, LinkedIn' },
  { id: 'portrait', label: '4:5', icon: '▮', hint: 'Vertical feed, Pinterest' },
  { id: 'vertical', label: '9:16', icon: '📱', hint: 'Reels, TikTok, Stories' },
  { id: 'widescreen', label: '16:9', icon: '▭', hint: 'YouTube, LinkedIn landscape' },
];

// Formato recomendado por red, para pre-seleccionar el shape correcto. "Formato"
// (shape de la imagen) NO es lo mismo que la plataforma: un 9:16 en el Feed de
// Instagram sale recortado. Esto sugiere; el usuario igual puede cambiarlo.
const FORMAT_BY_PLATFORM: Partial<Record<SocialNetwork, PreviewFormat>> = {
  instagram: 'square', facebook: 'square', twitter: 'square', linkedin: 'square',
  threads: 'square', gbp: 'square', bluesky: 'square', telegram: 'square',
  pinterest: 'portrait', tiktok: 'vertical', youtube: 'widescreen',
};

export function suggestFormat(platforms: SocialNetwork[]): PreviewFormat {
  if (platforms.length === 0) return 'square';
  const formats = new Set(platforms.map((p) => FORMAT_BY_PLATFORM[p] || 'square'));
  return formats.size === 1 ? [...formats][0] : 'square';
}

export const LANGUAGE_OPTIONS: { id: string; label: string; flag: string }[] = [
  { id: 'Spanish', label: 'Español', flag: '🇪🇸' },
  { id: 'English', label: 'English', flag: '🇺🇸' },
  { id: 'Portuguese', label: 'Português', flag: '🇧🇷' },
  { id: 'French', label: 'Français', flag: '🇫🇷' },
  { id: 'Italian', label: 'Italiano', flag: '🇮🇹' },
  { id: 'German', label: 'Deutsch', flag: '🇩🇪' },
];

// Mensajes rotativos durante la generación.
export const GEN_STATUS_MESSAGES = [
  'Analyzing your brand voice...',
  'Crafting compelling headlines...',
  'Selecting the perfect images...',
  'Writing engaging copy...',
  'Matching templates to content...',
  'Generating dynamic text overlays...',
  'Fine-tuning your CTAs...',
  'Building your content mix...',
  'Almost there, polishing final details...',
  'Putting the finishing touches...',
];
